import React, { useState } from "react";
import InputMask from "react-input-mask";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import logo from "images/lra_pink.svg";
import { ReactComponent as LoginIcon } from "feather-icons/dist/icons/log-in.svg";
import { ReactComponent as RegisterButtonIcon } from "feather-icons/dist/icons/book-open.svg";
import { api } from "supabase/database/api.ts";
import { createClient } from "supabase/client.ts";
import { toast } from 'react-toastify';
import {
  Container, Content, MainContainer, LogoLink, LogoImage, MainContent,
  Heading, FormContainer, Form, Input, SubmitButton, ValidationText,
  StyledModal, ModalHeading, ModalText, ModalHighlight, ModalButton,
  Spinner, LoadingWrapper
} from "./AuthStyles";
import tw from "twin.macro";

const DividerTextContainer = tw.div`my-12 border-b text-center relative`;

export default ({
  mode="",
  logoLinkUrl = "#",
  headingText = (mode === "reset") ? "Reset Password" : "Forgot Password",
  submitButtonText = "Reset Password",
  SubmitButtonIcon = LoginIcon,
  signupUrl = "/register",

}) => {
  const [id, setId] = useState("");
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [isLengthValid, setLengthValid] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasLowercase, setHasLowercase] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [resetPasswordEmailSent, setResetPassowordEmailSent] = useState(false);
  const [waitingForEmailResponse, setWaitingForEmailResponse] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showUpdateEmail, setShowUpdateEmail] = useState(false);
  const supabase = createClient();

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    setLengthValid(newPassword.length >= 8);
    setHasNumber(/\d/.test(newPassword));
    setHasUppercase(/[A-Z]/.test(newPassword));
    setHasLowercase(/[a-z]/.test(newPassword));
    setPasswordsMatch(newPassword === confirmPassword);
  };

  const handleConfirmPasswordChange = (event) => {
    const newConfirmPassword = event.target.value;
    setConfirmPassword(newConfirmPassword);
    setPasswordsMatch(password === newConfirmPassword);
  };

  const handlePasswordFocus = () => {
    setShowValidation(true);
  };

  const validateID = (scj_id) => {
    const regex = /^\d{8}-\d{5}$/;
    return regex.test(scj_id);
  }

  const handleReset = async (event) => {
    event.preventDefault();
    if (!validateID(id)) {
      toast.error('Please enter a valid ID number');
      return;
    }
    
    setWaitingForEmailResponse(true);
    try {
      const userData = await api.authQueries.getUserData({ reg_num: id });
      if (userData.length > 0) {
        setUserEmail(userData[0].email);
        setShowModal(true);
      } else {
        toast.error('User not found. Please check your ID number.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setWaitingForEmailResponse(false);
    }
  }

  const handleConfirmEmail = async () => {
    setWaitingForEmailResponse(true);
    try {
      sessionStorage.setItem('resetUserData', JSON.stringify({ 
        email: userEmail, 
        scj_id: id 
      }));
      
      toast.success('Email confirmed! Redirecting to password reset...');
      setShowModal(false);
      
      setTimeout(() => {
        window.location.href = '/reset-password';
      }, 1000);
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setWaitingForEmailResponse(false);
    }
  }

  const handleUpdateEmail = async () => {
    if (!newEmail) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setWaitingForEmailResponse(true);
    try {
      sessionStorage.setItem('resetUserData', JSON.stringify({ 
        email: newEmail, 
        scj_id: id,
        originalEmail: userEmail
      }));
      
      toast.success('Email updated! Redirecting to password reset...');
      setShowModal(false);
      setShowUpdateEmail(false);
      setNewEmail('');
      
      setTimeout(() => {
        window.location.href = '/reset-password';
      }, 1000);
    } catch (error) {
      toast.error('Failed to update email. Please try again.');
    } finally {
      setWaitingForEmailResponse(false);
    }
  }

  const handlePasswordReset = async (event) => {
    event.preventDefault();
    setWaitingForEmailResponse(true);

    try {
      const resetUserData = JSON.parse(sessionStorage.getItem('resetUserData') || '{}');
      
      if (!resetUserData.email) {
        toast.error('Session expired. Please start the password reset process again.');
        window.location.href = '/forgot-password';
        return;
      }

      const { data, error } = await supabase.functions.invoke("admin-reset-password", {
        body: {
          email: resetUserData.originalEmail || resetUserData.email,
          newPassword: password,
          ...(resetUserData.originalEmail ? { newEmail: resetUserData.email } : {})
        }
      });

      if (error) {
        toast.error(`Password reset failed: ${error.message}`);
      } else if (data?.success) {
        toast.success('Password updated successfully! Redirecting to login...');
        sessionStorage.removeItem('resetUserData');
        setTimeout(() => {
          window.location.href = '/login'
        }, 2000);
      } else {
        toast.error(`Password reset failed: ${data?.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setWaitingForEmailResponse(false);
    }
  }

  const isFormValid = passwordsMatch && isLengthValid && hasNumber && hasLowercase && hasUppercase;

  return(
    <AnimationRevealPage>
      <StyledModal
        isOpen={showModal}
        onRequestClose={() => !waitingForEmailResponse && setShowModal(false)}
        shouldCloseOnOverlayClick={!waitingForEmailResponse}
      >
        <ModalHeading>Confirm Email</ModalHeading>
        {!showUpdateEmail ? (
          <>
            <ModalText>Is this your current email?</ModalText>
            <ModalHighlight>{userEmail}</ModalHighlight>
            <ModalButton primary onClick={handleConfirmEmail} disabled={waitingForEmailResponse}>
              <LoadingWrapper>
                {waitingForEmailResponse && <Spinner />}
                <span>{waitingForEmailResponse ? 'Processing...' : 'Yes, Proceed to Reset Password'}</span>
              </LoadingWrapper>
            </ModalButton>
            <ModalButton onClick={() => setShowUpdateEmail(true)} disabled={waitingForEmailResponse}>
              Update Email
            </ModalButton>
          </>
        ) : (
          <>
            <ModalText>Enter your new email address:</ModalText>
            <Input
              type="email"
              placeholder="New Email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              tw="mt-0"
            />
            <ModalButton primary onClick={handleUpdateEmail} disabled={waitingForEmailResponse || !newEmail}>
              <LoadingWrapper>
                {waitingForEmailResponse && <Spinner />}
                <span>{waitingForEmailResponse ? 'Processing...' : 'Update & Proceed to Reset Password'}</span>
              </LoadingWrapper>
            </ModalButton>
            <ModalButton onClick={() => setShowUpdateEmail(false)} disabled={waitingForEmailResponse}>
              Cancel
            </ModalButton>
          </>
        )}
      </StyledModal>
      <Container>
        <Content>
          <MainContainer>
            <LogoLink href={logoLinkUrl}>
              <LogoImage src={logo} />
            </LogoLink>
            <MainContent>
              <Heading>{headingText}</Heading>
              <FormContainer>
                  { (mode === "reset") ?
                    <Form onSubmit={handlePasswordReset}>
                      <Input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={handlePasswordChange}
                        onFocus={handlePasswordFocus}
                        required
                      />
                      { showValidation && (
                        <div tw="mt-2">
                          <ValidationText valid={isLengthValid}>✓ At least 8 characters</ValidationText>
                          <ValidationText valid={hasNumber}>✓ At least 1 number</ValidationText>
                          <ValidationText valid={hasUppercase}>✓ At least 1 uppercase letter</ValidationText>
                          <ValidationText valid={hasLowercase}>✓ At least 1 lowercase letter</ValidationText>
                        </div>
                      )}
                      <Input
                        type="password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        required
                      />
                      { showValidation && (
                        <div tw="mt-2">
                          <ValidationText valid={passwordsMatch}>✓ Passwords Match</ValidationText>
                        </div>
                      )}

                      <SubmitButton type="submit" disabled={!isFormValid || waitingForEmailResponse}>
                        <SubmitButtonIcon className="icon" />
                        <LoadingWrapper>
                          {waitingForEmailResponse && <Spinner />}
                          <span className="text">{waitingForEmailResponse ? "Processing..." : submitButtonText}</span>
                        </LoadingWrapper>
                      </SubmitButton>
                    </Form> :
                    <Form onSubmit={handleReset}>
                      <InputMask mask={"99999999-99999"} type="text" value={id} onChange={(e) => setId(e.target.value)}  maskChar={null} maskPlaceholder={null}>
                        <Input type="scj_id" placeholder="ID Number (00400314-12345)" required/>
                      </InputMask>

                      {!resetPasswordEmailSent && (
                        <SubmitButton type="submit" disabled={waitingForEmailResponse}>
                          <SubmitButtonIcon className="icon" />
                          <LoadingWrapper>
                            {waitingForEmailResponse && <Spinner />}
                            <span className="text">{waitingForEmailResponse ? "Processing..." : submitButtonText}</span>
                          </LoadingWrapper>
                        </SubmitButton>
                      )}
                    </Form>
                  }

                <DividerTextContainer></DividerTextContainer>
                <p tw="mt-8 text-gray-600 text-center">
                  Don't have an account?{" "}
                  <a href={signupUrl}>
                    <SubmitButton>
                      <RegisterButtonIcon className="icon" />
                      <span className="text">{"Register"}</span>
                    </SubmitButton>
                  </a>
                </p>
              </FormContainer>
            </MainContent>
          </MainContainer>
        </Content>
      </Container>
    </AnimationRevealPage>
  );
}
